import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { notification } from 'antd';

import NozomiReconciliationContext from '@totem/components/administration/reconciliation/nozomi/NozomiReconciliationContext';
import {
  NozomiReconciliation,
  RowFilter,
} from '@totem/components/administration/reconciliation/nozomi/types';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { NOZOMI_RECONCILIATION_ENDPOINT } from '@totem/utilities/endpoints';

type Props = {
  children: ReactNode;
};

const NozomiReconciliationContainer = ({ children }: Props) => {
  const { getApiToken } = useContext(UserProfileContext);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<NozomiReconciliation | null>(null);
  const [filter, setFilter] = useState<RowFilter>('withAssets');
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [refreshCount, setRefreshCount] = useState(0);

  useEffect(() => {
    setLoading(true);

    fetch(NOZOMI_RECONCILIATION_ENDPOINT, {
      method: 'GET',
      headers: new Headers({ Authorization: `Bearer ${getApiToken()}` }),
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        return res.json();
      })
      .then((report: NozomiReconciliation) => setData(report))
      .catch(() => {
        notification.error({
          message: 'Failed to load the Nozomi reconciliation report.',
        });
      })
      .finally(() => setLoading(false));
  }, [getApiToken, refreshCount]);

  const onRefresh = useCallback(() => {
    setRefreshCount((count) => count + 1);
  }, []);

  return (
    <NozomiReconciliationContext.Provider
      value={{
        loading,
        data,
        filter,
        expandedKeys,
        onRefresh,
        onFilterChange: setFilter,
        onExpandedKeysChange: setExpandedKeys,
      }}
    >
      {children}
    </NozomiReconciliationContext.Provider>
  );
};

export default NozomiReconciliationContainer;
